import type { EventTypes, SportTypes } from '@racemap/sdk/schema/events';
import { RacemapAPIClient, type ShowValues } from '@racemap/utilities/api-client';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import type { SWRInfiniteResponse } from 'swr/infinite';

const apiClient = RacemapAPIClient.fromWindowLocation();

const fetcher = async ([
  _1,
  searchValue,
  filter,
  show,
  eventTypes,
  sportTypes,
  startingAfter,
  startingBefore,
  endingAfter,
  endingBefore,
  lastKey,
  pageSize,
]: [
  string,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  number,
]) => {
  return apiClient.getEventsPaged(
    {
      show: show ? (show.split(',') as Array<ShowValues>) : ['hidden'],
      filter,
      findByPartOfNameOrLocation: searchValue,
      findByEventTypes: eventTypes ? (eventTypes.split(',') as Array<EventTypes>) : undefined,
      findBySportTypes: sportTypes ? (sportTypes.split(',') as Array<SportTypes>) : undefined,
      startingBefore: startingBefore ? new Date(startingBefore) : undefined,
      startingAfter: startingAfter ? new Date(startingAfter) : undefined,
      endingBefore: endingBefore ? new Date(endingBefore) : undefined,
      endingAfter: endingAfter ? new Date(endingAfter) : undefined,
    },
    pageSize,
    lastKey,
  );
};

interface UseEventsPaginatedParams {
  searchValue?: string;
  filter?: string;
  show?: Array<ShowValues>;
  eventTypes?: Array<EventTypes>;
  sportTypes?: Array<SportTypes>;
  startingAfter?: Date;
  startingBefore?: Date;
  endingAfter?: Date;
  endingBefore?: Date;
  fetchDisabled?: boolean;
  pageSize?: number;
}

export type UseEventsPaginatedMutate = SWRInfiniteResponse<{
  data: Array<RacemapEvent>;
  lastKey: string;
}>['mutate'];

export const useEventsPaginated = ({
  searchValue,
  filter,
  show,
  eventTypes,
  sportTypes,
  startingAfter,
  startingBefore,
  endingAfter,
  endingBefore,
  pageSize = 30,
}: UseEventsPaginatedParams = {}): {
  data: Array<RacemapEvent>;
  isLoading: boolean;
  loadNextPage: () => void;
  hasNextPage: boolean;
  mutate: UseEventsPaginatedMutate;
  firstPageLoaded: boolean;
} => {
  const getKey = (
    _pageIndex: number,
    previousPageData: { data: Array<RacemapEvent>; lastKey: string } | null,
  ):
    | [
        string,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        number,
      ]
    | null => {
    return [
      'EVENTS_PAGED',
      searchValue,
      filter,
      show?.join(','),
      eventTypes?.join(','),
      sportTypes?.join(','),
      startingAfter?.toISOString(),
      startingBefore?.toISOString(),
      endingAfter?.toISOString(),
      endingBefore?.toISOString(),
      previousPageData?.lastKey,
      pageSize,
    ];
  };

  const { data, isLoading, size, setSize, mutate } = useSWRInfinite(getKey, fetcher, {
    revalidateAll: false,
    keepPreviousData: true,
  });

  const loadNextPage = useCallback(() => {
    setSize(size + 1);
  }, [setSize, size]);

  const events = useMemo(() => data?.flatMap((page) => page.data) || [], [data]);
  const firstPageLoaded = useMemo(() => data != null && data.length > 0, [data]);

  return {
    data: events,
    isLoading,
    loadNextPage,
    hasNextPage: data == null || data.at(-1)?.lastKey != null,
    mutate,
    firstPageLoaded,
  };
};
